import { arrayMove } from '@dnd-kit/sortable';
import type { NavigationItem } from '../../tokens/navigationPaths';

const EMPTY_GROUP_DROP_PREFIX = 'empty-group-drop:';
const UNGROUPED_GROUP_KEY = '__ungrouped__';

/** Droppable id for an empty group bucket (lets users drop items into a group with no rows). */
export function emptyGroupDropId(groupKey: string): string {
  return `${EMPTY_GROUP_DROP_PREFIX}${groupKey}`;
}

export function parseEmptyGroupDropId(overId: string): string | null {
  if (!overId.startsWith(EMPTY_GROUP_DROP_PREFIX)) {
    return null;
  }
  return overId.slice(EMPTY_GROUP_DROP_PREFIX.length);
}

/** Map from `groupLabelToKey` back to the group label stored on `LayoutSpec.group` (undefined = ungrouped). */
export function groupLabelForGroupKey(key: string): string | undefined {
  return key === UNGROUPED_GROUP_KEY ? undefined : key;
}

/** Stable map key for `group.label` (undefined = ungrouped bucket). */
export function groupLabelToKey(label: string | undefined): string {
  return label ?? UNGROUPED_GROUP_KEY;
}

/** Stable group identity for rearrangement; paths avoid collisions between groups with the same display label. */
export function getNavigationGroupKey(group: NavigationItem): string {
  return group.path ?? groupLabelToKey(group.label);
}

function groupLabelForKey(
  key: string,
  groupLabelsByKey: ReadonlyMap<string, string | undefined> | undefined
): string | undefined {
  return groupLabelsByKey?.has(key) ? groupLabelsByKey.get(key) : groupLabelForGroupKey(key);
}

export function findGroupKeyForId(orderByGroup: Map<string, string[]>, id: string): string | undefined {
  for (const [key, ids] of orderByGroup) {
    if (ids.includes(id)) {
      return key;
    }
  }
  return undefined;
}

export function getInitialOrderByGroup(groups: readonly NavigationItem[] | undefined): Map<string, string[]> {
  const orderByGroup = new Map<string, string[]>();
  for (const group of groups ?? []) {
    const children = (group.children ?? []) as NavigationItem[];
    const ids = children.flatMap(item => {
      const id = item.id ?? item.path;
      return item.isEditable && id != null ? [id] : [];
    });
    orderByGroup.set(getNavigationGroupKey(group), ids);
  }
  return orderByGroup;
}

export function getGroupLabelForSave(
  id: string,
  orderByGroup: Map<string, string[]>,
  groups: Map<string, string | undefined>,
  groupLabelsByKey: ReadonlyMap<string, string | undefined>,
  fallbackGroup: string | undefined
): string | undefined {
  if (groups.has(id)) {
    return groups.get(id);
  }
  const groupKey = findGroupKeyForId(orderByGroup, id);
  return groupKey != null && groupLabelsByKey.has(groupKey) ? groupLabelsByKey.get(groupKey) : fallbackGroup;
}

/**
 * Reorders `orderByGroup` / `groups` for a drag of `activeId` relative to `overId`.
 * Used from onDragOver (live) and onDragEnd (final sync). Returns null if nothing changes.
 */
export function applyReorder(
  orderByGroup: Map<string, string[]>,
  groups: Map<string, string | undefined>,
  activeId: string,
  overId: string,
  groupLabelsByKey?: ReadonlyMap<string, string | undefined>
): { orderByGroup: Map<string, string[]>; groups: Map<string, string | undefined> } | null {
  if (activeId === overId) {
    return null;
  }

  const emptyDropKey = parseEmptyGroupDropId(overId);
  if (emptyDropKey != null) {
    const next = new Map(orderByGroup);
    const activeKey = findGroupKeyForId(next, activeId);
    if (activeKey == null) {
      return null;
    }
    if (activeKey === emptyDropKey) {
      return null;
    }
    const sourceList = [...(next.get(activeKey) ?? [])];
    const from = sourceList.indexOf(activeId);
    if (from === -1) {
      return null;
    }
    sourceList.splice(from, 1);
    next.set(activeKey, sourceList);
    const targetList = [...(next.get(emptyDropKey) ?? []), activeId];
    next.set(emptyDropKey, targetList);

    const nextGroups = new Map(groups);
    nextGroups.set(activeId, groupLabelForKey(emptyDropKey, groupLabelsByKey));

    return { orderByGroup: next, groups: nextGroups };
  }

  const next = new Map(orderByGroup);
  const activeKey = findGroupKeyForId(next, activeId);
  const overKey = findGroupKeyForId(next, overId);
  if (activeKey == null || overKey == null) {
    return null;
  }

  if (activeKey === overKey) {
    const list = [...(next.get(activeKey) ?? [])];
    const oldIndex = list.indexOf(activeId);
    const newIndex = list.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
      return null;
    }
    next.set(activeKey, arrayMove(list, oldIndex, newIndex));
    return { orderByGroup: next, groups };
  }

  const sourceList = [...(next.get(activeKey) ?? [])];
  const targetList = [...(next.get(overKey) ?? [])];
  const from = sourceList.indexOf(activeId);
  const to = targetList.indexOf(overId);
  if (from === -1 || to === -1) {
    return null;
  }
  sourceList.splice(from, 1);
  targetList.splice(to, 0, activeId);
  next.set(activeKey, sourceList);
  next.set(overKey, targetList);

  const nextGroups = new Map(groups);
  nextGroups.set(activeId, groupLabelForKey(overKey, groupLabelsByKey));

  return { orderByGroup: next, groups: nextGroups };
}
